import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { SettingsHeader, PlatformIcon, SettingsGroup, SettingsDivider } from '@cubemeet/ui';
import { useI18n } from '../i18n';
import { SupportedLanguage } from '@cubemeet/core/i18n';
import { useSettingsTranslation } from '../modules/settings/i18n';

const LanguageSettingsPage = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useI18n();
  const { t: settingsT } = useSettingsTranslation(language);

  const handleLanguageChange = async (newLanguage: SupportedLanguage) => {
    try {
      await setLanguage(newLanguage);
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const languages: Array<{ code: SupportedLanguage; name: string; nativeName: string; flag: string }> = [
    {
      code: 'zh-CN',
      name: '简体中文',
      nativeName: '简体中文',
      flag: '🇨🇳'
    },
    {
      code: 'zh-TW', 
      name: '繁體中文',
      nativeName: '繁體中文',
      flag: '🇹🇼'
    },
    {
      code: 'en',
      name: 'English',
      nativeName: 'English', 
      flag: '🇺🇸'
    },
    {
      code: 'ja',
      name: '日本語',
      nativeName: '日本語',
      flag: '🇯🇵'
    },
    {
      code: 'ko',
      name: '한국어',
      nativeName: '한국어',
      flag: '🇰🇷'
    }
  ];

  return (
    <View style={styles.container}>
      <SettingsHeader 
        title={settingsT('items.languageSettings.title')} 
        onBack={() => navigate(-1)}
      />

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={styles.contentWrapper}>
          <View style={styles.headerSection}>
            <Text style={styles.sectionTitle}>{settingsT('items.languageSettings.sectionTitle')}</Text>
            <Text style={styles.sectionSubtitle}>{settingsT('items.languageSettings.sectionSubtitle')}</Text>
          </View>

          <SettingsGroup>
            {languages.map((lang, index) => (
              <React.Fragment key={lang.code}>
                <TouchableOpacity
                  style={[
                    styles.languageItem,
                    language === lang.code && styles.languageItemSelected
                  ]}
                  onPress={() => handleLanguageChange(lang.code)}
                >
                  <View style={styles.languageLeft}>
                    <Text style={styles.languageFlag}>{lang.flag}</Text>
                    <View style={styles.languageInfo}>
                      <Text style={[
                        styles.languageName,
                        language === lang.code && styles.languageNameSelected
                      ]}>
                        {lang.name}
                      </Text>
                      <Text style={styles.languageNative}>{lang.nativeName}</Text>
                    </View>
                  </View>
                  
                  {language === lang.code && (
                    <PlatformIcon name="check" size={20} color="#4ECDC4" family="MaterialIcons" />
                  )}
                </TouchableOpacity>
                {index < languages.length - 1 && <SettingsDivider />}
              </React.Fragment>
            ))}
          </SettingsGroup>

          <View style={styles.note}>
            <PlatformIcon name="info" size={16} color="#666" family="MaterialIcons" />
            <Text style={styles.noteText}>
              {settingsT('items.languageSettings.note')}
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f6f7f8',
    height: '100%',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 40,
  },
  contentWrapper: {
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 15,
    paddingTop: 20,
  },
  headerSection: {
    marginBottom: 15,
    paddingHorizontal: 5,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  languageItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#fff',
    cursor: 'pointer', // Web specific
  },
  languageItemSelected: {
    backgroundColor: '#E6F7F5',
  },
  languageLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  languageFlag: {
    fontSize: 24,
    marginRight: 16,
  },
  languageInfo: {
    flex: 1,
  },
  languageName: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  languageNameSelected: {
    color: '#4ECDC4',
    fontWeight: '600',
  },
  languageNative: {
    fontSize: 14,
    color: '#666',
  },
  note: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 12,
    paddingHorizontal: 5,
    gap: 8,
  },
  noteText: {
    flex: 1,
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
});

export default LanguageSettingsPage;
